import { cva, type VariantProps } from "class-variance-authority";
import { Check } from "lucide-react";
import { cn } from "../utils";

export const chipBase = cva(
  "rounded-2xl p-4 pr-5 min-h-[44px] text-size-chips text-left border border-chips-border",
  {
    variants: {
      $variant: {
        "single-select": "inline-flex items-center gap-1.5",
        "multi-select": "flex w-content items-center gap-3",
      },
    },
    defaultVariants: {
      $variant: "single-select",
    },
  },
);

export const chipInteractive =
  "transition-all active:scale-[0.98] focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 hover:border-primary/40 cursor-pointer";

export const chipDisabled = "cursor-not-allowed opacity-50";

export const chipReadonly = "cursor-default border-chips-border/80 opacity-80";

export type ChipBaseVariants = VariantProps<typeof chipBase>;

type ChipVariant = "single-select" | "multi-select";

type ChipProps = {
  label: string;
  selected?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  variant?: ChipVariant;
  secondary?: boolean;
  onClick?: () => void;
  role?: "radio" | "checkbox" | "button";
  "aria-checked"?: boolean;
  /** Debug-only: highlight this chip as an archetype probe */
  probeHighlight?: boolean;
};

export function shouldStackChips(question: {
  multi_select?: boolean;
  options: { label: string }[];
}) {
  if (question.multi_select) return true;
  const totalChars = question.options.reduce((sum, opt) => sum + opt.label.length, 0);
  return totalChars > 20;
}

export function Chip({
  label,
  selected = false,
  disabled = false,
  readOnly = false,
  variant = "single-select",
  secondary = false,
  onClick,
  role = "button",
  "aria-checked": ariaChecked,
  probeHighlight = false,
}: ChipProps) {
  const baseClasses = cn(
    chipBase({ $variant: variant }),
    probeHighlight && "border-red-950/25 hover:border-red-900/35",
    !probeHighlight && !readOnly && !disabled && chipInteractive,
    probeHighlight &&
      !readOnly &&
      !disabled &&
      "transition-all active:scale-[0.98] focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 cursor-pointer",
    disabled && chipDisabled,
    readOnly && chipReadonly,
  );

  if (variant === "multi-select") {
    return (
      <button
        type="button"
        role={role}
        aria-checked={ariaChecked ?? selected}
        onClick={onClick}
        disabled={disabled}
        className={baseClasses}
      >
        <span
          aria-hidden="true"
          className={`flex size-5 shrink-0 items-center justify-center rounded transition-colors border-primary/20 ${
            selected ? "bg-checkbox-filled" : "border-[1.6px]"
          }`}
        >
          {selected && <Check size={12} className="text-white" strokeWidth={3} />}
        </span>
        <span className={secondary && !selected ? "text-text-secondary" : "text-text-question"}>
          {label}
        </span>
      </button>
    );
  }

  return (
    <button
      type="button"
      role={role}
      aria-checked={ariaChecked ?? selected}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        baseClasses,
        secondary && !selected ? "text-text-secondary" : "text-text-question",
      )}
    >
      {selected && <Check size={14} className="text-accent" strokeWidth={2.5} />}
      {label}
    </button>
  );
}
